// src/hooks/useDogDayManager.js
import { useEffect, useCallback, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useAppState } from "@/context/AppStateContext";
import { useUser } from "@/context/UserContext";
import { useDate } from "@/context/DateContext";
import { useDogList } from "@/context/DogListContext";
import { useHistory } from "@/context/HistoryContext";
 import { useConfig } from "@/context/ConfigContext";
 import { useSubscription } from "@/context/SubscriptionContext";

 import { loadFromStorage, saveToStorage, loadFromSessionStorage, saveToSessionStorage } from "@/lib/storage";
import { LOCAL_STORAGE_KEYS } from "@/config/constants";
import { isValidDate } from "@/lib/dateUtils";
import { getWorkspaceId } from "@/lib/workspaceUtils";
import { auth } from "@/firebase";

import {
  initializeDogData,
  handleDateChange as handleDateChangeAction,
} from "./useDogDayManagerInitializer";
import { getDogsFor } from "@/lib/dogStateHelpers";
import { makeDogStateKey } from "@/lib/dogIdentity";

import {
  handleUndo as undoAction,
  handleRedo as redoAction,
  handleUserChange as userChangeAction,
  handleClearAll as clearAllAction,
  handleCheckAll as checkAllAction,
  handleDogClick as dogClickAction,
  handleUrgentClick as urgentClickAction,
  handleAddDogToList as addDogAction,
  handleDeleteDog as deleteDogAction,
  handleEditDogInList as editDogAction,
  handleReorderDogs as reorderDogsAction,
  calculateDailyDogCount as calculateCountAction,
  recordSnapshot as recordSnapshotAction,
  getInitialDayDataForUser,
  extendFixedDogsWindow as extendWindowAction,
  applyBatchCanceledDays as applyBatchCanceledDaysAction,
} from "./dogDayManagerActions";
import { handleBulkUpdateFromPDF as bulkUpdateAction } from "./dogDataParsers";

const MAX_HISTORY_LENGTH = 50;
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export function useDogDayManager(
  lastSavedDaysListRef,
  markDirty,
  markDeletedAll,
  clearDeletedAll,
  editLocksRef,
  editRevisionCounterRef,
  daysListRef
) {

  const { toast } = useToast();
  const { setIsLoading } = useAppState();
  const { selectedUser, setSelectedUser } = useUser();
  const { currentDate, setCurrentDate, selectedDayKey, setSelectedDayKey } =
    useDate();
  const {
    daysList,
    setDaysList,
    completedDogs,
    setCompletedDogs,
    urgentDogs,
    setUrgentDogs,
  } = useDogList();
   const { history, setHistory, currentPosition, setCurrentPosition } =
     useHistory();
    const { config, workspaceId } = useConfig();
    const { permissions } = useSubscription();

   // Ref para prevenir loops de recordSnapshot durante restore
  const isRestoringRef = useRef(false);
  // Refs para history y posición - siempre actuales, sin stale closure
  const historyRef = useRef([]);
  const currentPositionRef = useRef(0);

  /* --------------------- obtener inicial para un día/usuario --------------------- */
  const getInitialDayData = useCallback(
    (dayKeyForInitial) => {
      const permanentlyDeletedDogsRaw = loadFromStorage(
        LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
        [],
        selectedUser
      );
      const permanentlyDeletedDogs = Array.isArray(permanentlyDeletedDogsRaw)
        ? permanentlyDeletedDogsRaw
        : [];
      return getInitialDayDataForUser(
        dayKeyForInitial,
        selectedUser,
        permanentlyDeletedDogs
      );
    },
    [selectedUser]
  );

 /* --------------------------------- snapshot --------------------------------- */
const recordSnapshot = useCallback(
  (overrideDays, overrideCompleted, overrideUrgent) => {
    // Bloquear durante restore para prevenir loops
    if (isRestoringRef.current) {
      console.log("[HIST][REC] Blocked during restore");
      return;
    }

    // Snapshot con overrides explícitos (estado nuevo) o fallback a closure (add-dog)
    const snapshot = {
      daysList: deepClone(overrideDays ?? daysList),
      completedDogs: deepClone(overrideCompleted ?? completedDogs),
      urgentDogs: deepClone(overrideUrgent ?? urgentDogs),
    };

    // Leer posición e historia desde refs (siempre actuales, sin stale closure)
    const capturedPos = currentPositionRef.current;
    const prevHistory = historyRef.current;

    // Cortar redo stack y añadir nuevo snapshot
    const safePos = Math.min(capturedPos, prevHistory.length - 1);
    const base = prevHistory.slice(0, safePos + 1);
    let updated = [...base, snapshot];
    if (updated.length > MAX_HISTORY_LENGTH) {
      updated = updated.slice(updated.length - MAX_HISTORY_LENGTH);
    }
    const newPos = updated.length - 1;

    // Actualizar refs antes de que React re-renderice
    historyRef.current = updated;
    currentPositionRef.current = newPos;

    // Actualizar estado React (para UI: canUndo/canRedo)
    setHistory(updated);
    setCurrentPosition(newPos);

    console.log("[HIST][REC]", { safePos, newPos, prevLen: prevHistory.length, updatedLen: updated.length });
  },
  [daysList, completedDogs, urgentDogs, setHistory, setCurrentPosition]
);


  /* ----------------------------- carga inicial app ----------------------------- */
  // Cargar selectedUser del sessionStorage en lugar del localStorage
  // Esto asegura que cada pestaña pueda tener un perfil diferente
  useEffect(() => {
    const userFromSession = loadFromSessionStorage(
      LOCAL_STORAGE_KEYS.SELECTED_USER,
      null
    );
    if (userFromSession && userFromSession !== selectedUser) {
      setSelectedUser(userFromSession);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);

    // ✅ FIRESTORE ES LA FUENTE DE VERDAD - No cargamos desde localStorage
    console.log('[INIT] Iniciando con estados vacíos - Firestore poblará los datos');
    const { initialDaysList, initialCompletedDogs, initialUrgentDogs } =
      initializeDogData(selectedUser, selectedDayKey, currentDate);

    // initialDaysList viene vacío desde initializeDogData
    setDaysList(initialDaysList); // {}
    setCompletedDogs(initialCompletedDogs);
    setUrgentDogs(initialUrgentDogs);

    const initialSnapshot = {
      daysList: deepClone(initialDaysList),
      completedDogs: deepClone(initialCompletedDogs),
      urgentDogs: deepClone(initialUrgentDogs),
    };

    setHistory([initialSnapshot]);
    setCurrentPosition(0);
    historyRef.current = [initialSnapshot];
    currentPositionRef.current = 0;
    setIsLoading(false);
  }, [
    selectedUser,
    setDaysList,
    setCompletedDogs,
    setUrgentDogs,
    setHistory,
    setCurrentPosition,
    setIsLoading,
  ]);

  /* ------------------------------- persistencias ------------------------------ */
  useEffect(() => {
    // IMPORTANTE: selectedUser se guarda en sessionStorage, NO en localStorage
    // Esto permite que cada pestaña tenga su propio perfil sin sincronizarse
    saveToSessionStorage(LOCAL_STORAGE_KEYS.SELECTED_USER, selectedUser);
  }, [selectedUser]);

  useEffect(() => {
    if (isValidDate(currentDate)) {
      saveToStorage(
        LOCAL_STORAGE_KEYS.CURRENT_DATE,
        currentDate.toISOString(),
        selectedUser
      );
    }
  }, [currentDate, selectedUser]);

  // ✅ NO GUARDAR daysList EN LOCALSTORAGE - Solo Firestore debe persistir
  useEffect(() => {
    if (currentPosition >= 0 && Object.keys(daysList).length > 0) {
      // DEBUG: Log estructura de daysList para comparar flujos
      const sampleDateKey = Object.keys(daysList)[0];
      console.log("[DEBUG_DAYLIST] daysList structure:", {
        totalKeys: Object.keys(daysList).length,
        sampleDateKey: sampleDateKey,
        sampleDayContent: daysList[sampleDateKey],
        allDateKeys: Object.keys(daysList).slice(0, 10), // Primeras 10 claves
      });
      console.log('[STORAGE] daysList NO se guarda en localStorage - solo en Firestore');
    }
  }, [daysList, selectedUser, currentPosition]);

  // ✅ NO persistir completedDogs/urgentDogs en localStorage
  // Firestore es la fuente de verdad y sincroniza en tiempo real vía subscribeStates
  // Esta forma previene herencia de estados azul/rojo de sesiones anteriores
  useEffect(() => {
    // No guardar completedDogs en localStorage - causaba conflictos con Firestore
    // La sincronización ocurre automáticamente via subscribeStates en AppCore.jsx
  }, [completedDogs, selectedUser, currentPosition]);

  useEffect(() => {
    // No guardar urgentDogs en localStorage - causaba conflictos con Firestore
    // La sincronización ocurre automáticamente via subscribeStates en AppCore.jsx
  }, [urgentDogs, selectedUser, currentPosition]);

  /* ---------------------------------- acciones --------------------------------- */
  const handleDateChange = useCallback(
    (date) => {
      handleDateChangeAction(
        date,
        setCurrentDate,
        setSelectedDayKey,
        setDaysList,
        getInitialDayData,
        selectedUser
      );
    },
    [
      setCurrentDate,
      setSelectedDayKey,
      setDaysList,
      getInitialDayData,
      selectedUser,
    ]
  );

  const handleUndo = useCallback(() => {
    if (currentPositionRef.current <= 0) return;
    
    // Bloquear recordSnapshot durante restore
    isRestoringRef.current = true;
    const newPosition = currentPositionRef.current - 1;
    const previousState = historyRef.current[newPosition];
    
    console.log("[HIST][UNDO]", {
      from: currentPositionRef.current,
      to: newPosition,
      histLen: historyRef.current.length
    });
    
    setDaysList(deepClone(previousState.daysList));
    setCompletedDogs(deepClone(previousState.completedDogs));
    setUrgentDogs(deepClone(previousState.urgentDogs));
    currentPositionRef.current = newPosition;
    setCurrentPosition(newPosition);
    toast({
      title: "Acción deshecha",
      description: "Se ha restaurado el estado anterior."
    });
    
    // Desbloquear en siguiente tick para permitir que efectos se ejecuten
    queueMicrotask(() => { isRestoringRef.current = false; });
  }, [
    setDaysList,
    setCompletedDogs,
    setUrgentDogs,
    setCurrentPosition,
    toast,
  ]);

  const handleRedo = useCallback(() => {
    if (currentPositionRef.current >= historyRef.current.length - 1) return;
    
    // Bloquear recordSnapshot durante restore
    isRestoringRef.current = true;
    const newPosition = currentPositionRef.current + 1;
    const nextState = historyRef.current[newPosition];
    
    console.log("[HIST][REDO]", {
      from: currentPositionRef.current,
      to: newPosition,
      histLen: historyRef.current.length
    });
    
    setDaysList(deepClone(nextState.daysList));
    setCompletedDogs(deepClone(nextState.completedDogs));
    setUrgentDogs(deepClone(nextState.urgentDogs));
    currentPositionRef.current = newPosition;
    setCurrentPosition(newPosition);
    toast({
      title: "Acción rehecha",
      description: "Se ha restaurado el siguiente estado."
    });
    
    // Desbloquear en siguiente tick para permitir que efectos se ejecuten
    queueMicrotask(() => { isRestoringRef.current = false; });
  }, [
    setDaysList,
    setCompletedDogs,
    setUrgentDogs,
    setCurrentPosition,
    toast,
  ]);

  const handleUserChange = useCallback(() => {
    userChangeAction(
      selectedUser,
      setSelectedUser,
      setCurrentDate,
      setSelectedDayKey,
      toast,
      config.profiles
    );
  }, [
    selectedUser,
    toast,
    setSelectedUser,
    setCurrentDate,
    setSelectedDayKey,
    config.profiles,
  ]);

  const handleClearAll = useCallback(() => {
    clearAllAction(
      daysList,
      selectedDayKey,
      completedDogs,
      urgentDogs,
      setCompletedDogs,
      setUrgentDogs,
      recordSnapshot,
      toast,
      getInitialDayData,
      selectedUser
    );
  }, [
    daysList,
    selectedDayKey,
    completedDogs,
    urgentDogs,
    recordSnapshot,
    toast,
    getInitialDayData,
    setCompletedDogs,
    setUrgentDogs,
    selectedUser,
  ]);

  const handleCheckAll = useCallback(() => {
    checkAllAction(
      daysList,
      selectedDayKey,
      completedDogs,
      urgentDogs,
      setCompletedDogs,
      setUrgentDogs,
      recordSnapshot,
      toast,
      getInitialDayData,
      selectedUser
    );
  }, [
    daysList,
    selectedDayKey,
    completedDogs,
    urgentDogs,
    recordSnapshot,
    toast,
    getInitialDayData,
    setCompletedDogs,
    setUrgentDogs,
    selectedUser,
  ]);

  const handleDogClick = useCallback(
    // Fase 2: dogId opcional (identidad estable). Si se provee, la clave de
    // estado se construye por id; si no, fallback legacy por nombre.
    (dogName, dogId) => {
      dogClickAction(
        selectedDayKey,
        dogName,
        completedDogs,
        urgentDogs,
        setCompletedDogs,
        setUrgentDogs,
        daysList,
        recordSnapshot,
        dogId
      );
    },
    [
      selectedDayKey,
      completedDogs,
      urgentDogs,
      daysList,
      recordSnapshot,
      setCompletedDogs,
      setUrgentDogs,
    ]
  );

  const handleUrgentClick = useCallback(
    // Fase 2: dogId opcional (identidad estable). Si se provee, la clave de
    // estado se construye por id; si no, fallback legacy por nombre.
    (dogName, dogId) => {
      urgentClickAction(
        selectedDayKey,
        dogName,
        urgentDogs,
        completedDogs,
        setUrgentDogs,
        setCompletedDogs,
        daysList,
        recordSnapshot,
        dogId
      );
    },
    [
      selectedDayKey,
      urgentDogs,
      completedDogs,
      daysList,
      recordSnapshot,
      setUrgentDogs,
      setCompletedDogs,
    ]
  );

  // >>> ÚNICA definición correcta (no la dupliques)
  const handleAddDogToList = useCallback(
    (newDogName, newDogWhatsApp, dogDaysAsDateKeys, dogCount, isFixed, locationUrl) => {
      const name =
        typeof newDogName === "string" ? newDogName.trim() : "";
      if (!name) {
        console.warn("[handleAddDogToList] nombre inválido:", newDogName);
        return;
      }

      const wa =
        typeof newDogWhatsApp === "string" ? newDogWhatsApp : "";

      const location =
        typeof locationUrl === "string" ? locationUrl : "";

      const daysKeys =
        Array.isArray(dogDaysAsDateKeys) && dogDaysAsDateKeys.length > 0
          ? dogDaysAsDateKeys
          : [selectedDayKey];

      const count = Number.isFinite(dogCount) && dogCount > 0 ? dogCount : 1;
      const fixed = Boolean(isFixed);

      return addDogAction(
        name,
        wa,
        daysKeys,
        count,
        fixed,
        daysList,
        completedDogs,
        urgentDogs,
        setDaysList,
        recordSnapshot,
        toast,
        getInitialDayData,
        selectedDayKey,
        selectedUser,
        location,
        lastSavedDaysListRef,  // ✅ Pasar ref para persistencia inmediata
        workspaceId  // ✅ workspace compartido canónico desde ConfigContext
      );
    },
    [
      daysList,
      completedDogs,
      urgentDogs,
      recordSnapshot,
      toast,
      getInitialDayData,
      setDaysList,
      selectedDayKey,
      selectedUser,
      lastSavedDaysListRef
    ]
  );

  const handleDeleteDog = useCallback(
    async (dogName, scope, dayKey, dogId) => {
      await deleteDogAction(
        dogName,
        daysList,
        completedDogs,
        urgentDogs,
        dayKey || selectedDayKey,
        setDaysList,
        setCompletedDogs,
        setUrgentDogs,
        recordSnapshot,
        toast,
        selectedUser,
        scope,
        workspaceId,
        markDeletedAll,
        clearDeletedAll,
        dogId // Fase 2: identidad estable opcional, pasada desde la UI
      );
    },
    [
      daysList,
      completedDogs,
      urgentDogs,
      recordSnapshot,
      toast,
      selectedDayKey,
      setDaysList,
      setCompletedDogs,
      setUrgentDogs,
      selectedUser,
      markDeletedAll,
      clearDeletedAll,
      workspaceId
    ]
  );


   const handleEditDogInList = useCallback(
     (
       oldName,
       newName,
       newWhatsApp,
       newCount,
       presenceDateKeys,
       isFixed,
       notesByDay,
       locationUrl,
       onlyThisWeek = true,
       schedules = [],
       // Switch INDEPENDIENTE "Solo esta semana" de la sección de notas
       // informativas, completamente separado de onlyThisWeek (que sigue
       // aplicando solo a "Días de actividad"/whatsapp/localización/
       // schedules). Ver dogDayManagerActions.handleEditDogInList para el
       // detalle de su comportamiento (nota puntual vs. recurrente).
       notesScopeByDay = {},
       terminatedNoteDays = [],
       // Fase 2: identidad estable (dog.id) del cliente editado, si existe.
       // Se usa solo como preferencia para resolver la identidad al
       // sincronizar la plantilla; no cambia la localización de filas
       // por nombre (searchName), que sigue igual.
       dogId
     ) => {
       editDogAction(
         oldName,
         newName,
         newWhatsApp,
         newCount,
         presenceDateKeys,
         isFixed,
         notesByDay,
         locationUrl,
         daysList,
         completedDogs,
         urgentDogs,
         setDaysList,
         setCompletedDogs,
         setUrgentDogs,
         recordSnapshot,
         toast,
         getInitialDayData,
         selectedDayKey,
         selectedUser,
         onlyThisWeek,
         schedules,
         notesScopeByDay,
         terminatedNoteDays,
         dogId,
         // Coordinación edición/autoguardado/snapshots (weekOfChange):
         // refs compartidos con AppCore, enhebrados sin crear copias.
         editLocksRef,
         editRevisionCounterRef,
         daysListRef,
         lastSavedDaysListRef
       );
     },
     [
       daysList,
       completedDogs,
       urgentDogs,
       recordSnapshot,
       toast,
       getInitialDayData,
       setDaysList,
       setCompletedDogs,
       setUrgentDogs,
       selectedDayKey,
       selectedUser,
       editLocksRef,
       editRevisionCounterRef,
       daysListRef,
       lastSavedDaysListRef,
     ]
   );



  const handleReorderDogs = useCallback(
    (reorderedDogsForDay) => {
      reorderDogsAction(
        reorderedDogsForDay,
        selectedDayKey,
        daysList,
        completedDogs,
        urgentDogs,
        setDaysList,
        recordSnapshot,
        toast,
        selectedUser
      );
    },
    [
      daysList,
      selectedDayKey,
      completedDogs,
      urgentDogs,
      recordSnapshot,
      toast,
      setDaysList,
      selectedUser,
    ]
  );

  const handleBulkUpdateFromPDF = useCallback(
    async (pdfText) => {
      // ✅ Limpiar TODOS los estados (azul/rojo) antes de importar el PDF.
      // El PDF puede afectar hasta 26 semanas × 5 días → limpiar todo es lo correcto.
      // subscribeStates re-sincronizará los estados reales desde Firestore inmediatamente.
      setCompletedDogs({});
      setUrgentDogs({});

       return await bulkUpdateAction(
         pdfText,
         daysList,
         setDaysList,
         recordSnapshot,
         toast,
         selectedUser,
         handleAddDogToList,
         selectedDayKey,
         currentDate,
         markDirty,
         permissions.canUseUnlimitedClients
       );
    },
     [daysList, setDaysList, recordSnapshot, toast, selectedUser, handleAddDogToList,
      selectedDayKey, currentDate, markDirty, setCompletedDogs, setUrgentDogs, permissions.canUseUnlimitedClients]
  );

  const calculateDailyDogCount = useCallback(
    (dateKey) => {
      // UI_DAY_COUNTER: Log temporal para validar que se leen desde composite key
      console.error("[UI_DAY_COUNTER]", JSON.stringify({
        profile: selectedUser,
        dayKey: dateKey,
        legacyLen: (daysList?.[dateKey] || []).length,
        compositeLen: (daysList?.[`${selectedUser}::${dateKey}`] || []).length,
        urgentKeysSample: Object.keys(urgentDogs || {}).slice(0, 3),
      }));

      // Usa getDogsFor para leer con composite key, pero mantén EXACTAMENTE la misma lógica de cálculo
      const dogsForDay = getDogsFor(daysList, selectedUser, dateKey);
      return dogsForDay.reduce((total, dog) => {
        const isUrgent = urgentDogs[makeDogStateKey(dateKey, dog)];
        return isUrgent ? total : total + (dog.count || 1);
      }, 0);
    },
    [daysList, urgentDogs, selectedUser]
  );

  // UI_DOGS_READ: Log temporal para validar que se leen desde composite key
  console.error('[UI_DOGS_READ]', JSON.stringify({
    profile: selectedUser,
    dayKey: selectedDayKey,
    legacyLen: (daysList?.[selectedDayKey]||[]).length,
    compositeLen: (daysList?.[`${selectedUser}::${selectedDayKey}`]||[]).length
  }));

 const currentDogListToDisplay = [...getDogsFor(daysList, selectedUser, selectedDayKey)]
  .sort((a, b) => (a.originalOrder || 0) - (b.originalOrder || 0));

  const canUndo = currentPosition > 0;
  const canRedo = currentPosition < history.length - 1;

  // Crear callback batch para cancelaciones: delega a applyBatchCanceledDaysAction
  // con los estados actuales capturados por closure (completedDogs, urgentDogs,
  // daysList, setUrgentDogs, recordSnapshot). Firma compatible con EditDogForm.
  // Ahora async: persiste PRIMERO en Firestore (transacción única atómica) y
  // solo si tiene éxito actualiza el estado local. Devuelve la promesa para
  // que EditDogForm pueda esperarla y decidir si cierra el diálogo o no.
  const handleApplyCanceledDaysBatch = useCallback(
    (dogName, dogId, addedDateKeys, removedDateKeys, existsByDateKey) => {
      const workspaceId = getWorkspaceId(auth);
      return applyBatchCanceledDaysAction(
        dogName,
        dogId,
        addedDateKeys,
        removedDateKeys,
        urgentDogs,
        setUrgentDogs,
        daysList,
        recordSnapshot,
        completedDogs,
        workspaceId,
        selectedUser,
        existsByDateKey
      );
    },
    [urgentDogs, setUrgentDogs, daysList, recordSnapshot, completedDogs, selectedUser]
  );


  return {
    selectedUser,
    currentDate,
    selectedDayKey,
    handleDateChange,
    completedDogs,
    urgentDogs,
    daysList,
    currentDogList: currentDogListToDisplay,
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    handleUserChange,
    handleClearAll,
    handleCheckAll,
    handleDogClick,
    handleUrgentClick,
    handleAddDogToList,
    handleDeleteDog,
    handleEditDogInList,
    handleReorderDogs,
    calculateDailyDogCount,
    getInitialDayData,
    handleBulkUpdateFromPDF,
    handleApplyCanceledDaysBatch,
  };
}
